import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Search,
  FileText,
  X,
  Image,
  Download,
  Upload,
  Loader2,
  Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminReferenceSheets() {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [topicFilter, setTopicFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    topic_key: '',
    subtopic_key: '',
    png_urls: [],
    pdf_url: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    fetchSheets();
  }, [search, topicFilter, statusFilter, pagination.page]);

  useEffect(() => {
    if (formData.topic_key) {
      fetchSubtopics(formData.topic_key);
    } else {
      setSubtopics([]);
    }
  }, [formData.topic_key]);

  const fetchTopics = async () => {
    try {
      const res = await fetch(`${API}/api/admin/topics/all`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTopics(data);
      }
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  };

  const fetchSubtopics = async (topicKey) => {
    try {
      const res = await fetch(`${API}/api/admin/subtopics/${encodeURIComponent(topicKey)}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubtopics(data);
      }
    } catch (err) {
      console.error('Failed to fetch subtopics:', err);
    }
  };

  const fetchSheets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: 20
      });
      if (search) params.append('search', search);
      if (topicFilter !== 'all') params.append('topic', topicFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await fetch(`${API}/api/admin/reference-sheets?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch sheets');
      const data = await res.json();
      setSheets(data.sheets || []);
      setPagination(data.pagination || { page: 1, total: 0, total_pages: 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, topicFilter, statusFilter, pagination.page]);

  const openCreateDialog = () => {
    setSelectedSheet(null);
    setFormData({
      title: '',
      description: '',
      topic_key: '',
      subtopic_key: '',
      png_urls: [],
      pdf_url: '',
      status: 'draft'
    });
    setSubtopics([]);
    setShowEditDialog(true);
  };

  const openEditDialog = async (sheet) => {
    try {
      const res = await fetch(`${API}/api/admin/reference-sheets/${sheet.sheet_id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch sheet details');
      const data = await res.json();
      
      setSelectedSheet(data);
      setFormData({
        title: data.title || '',
        description: data.description || '',
        topic_key: data.topic_key || '',
        subtopic_key: data.subtopic_key || '',
        png_urls: data.png_urls || [],
        pdf_url: data.pdf_url || '',
        status: data.status || 'draft'
      });
      
      if (data.topic_key) {
        fetchSubtopics(data.topic_key);
      }
      
      setShowEditDialog(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // File upload handlers
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newUrls = [];
    
    for (const file of files) {
      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error(`${file.name}: Only PNG, JPG, WEBP files are allowed`);
        continue;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const res = await fetch(`${API}/api/admin/reference-sheets/upload-image`, {
          method: 'POST',
          credentials: 'include',
          body: formDataUpload
        });
        
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail || 'Upload failed');
        }
        
        const data = await res.json();
        newUrls.push(`${API}${data.url}`);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    
    if (newUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        png_urls: [...prev.png_urls, ...newUrls]
      }));
      toast.success(`${newUrls.length} image(s) uploaded successfully`);
    }
    
    setUploading(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    // Validate file size (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('PDF file too large (max 20MB)');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await fetch(`${API}/api/admin/reference-sheets/upload-pdf`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await res.json();
      setFormData(prev => ({
        ...prev,
        pdf_url: `${API}${data.url}`
      }));
      toast.success('PDF uploaded successfully');
    } catch (err) {
      toast.error(`Failed to upload PDF: ${err.message}`);
    }
    
    setUploading(false);
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  const removeImage = (idx) => {
    setFormData(prev => ({
      ...prev,
      png_urls: prev.png_urls.filter((_, i) => i !== idx)
    }));
  };

  const removePdf = () => {
    setFormData(prev => ({ ...prev, pdf_url: '' }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.topic_key) {
      toast.error('Topic is required');
      return;
    }
    if (formData.png_urls.length === 0) {
      toast.error('At least one image is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        subtopic_key: formData.subtopic_key || null,
        pdf_url: formData.pdf_url || null
      };
      
      const url = selectedSheet
        ? `${API}/api/admin/reference-sheets/${selectedSheet.sheet_id}`
        : `${API}/api/admin/reference-sheets`;
      
      const res = await fetch(url, {
        method: selectedSheet ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(selectedSheet ? 'Reference sheet updated' : 'Reference sheet created');
      setShowEditDialog(false);
      fetchSheets();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSheet) return;
    
    try {
      const res = await fetch(`${API}/api/admin/reference-sheets/${selectedSheet.sheet_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Failed to delete');
      
      toast.success('Reference sheet deleted');
      setShowDeleteDialog(false);
      setSelectedSheet(null);
      fetchSheets();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getTopicDisplay = (key) => {
    const topic = topics.find(t => t.key === key);
    return topic?.display || key;
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-100 rounded-lg">
                <FileText className="w-8 h-8 text-teal-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Reference Sheets</h1>
                <p className="text-slate-500">Create and manage reference sheet library</p>
              </div>
            </div>
            <Button
              onClick={openCreateDialog}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="create-sheet-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Reference Sheet
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search sheets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={topicFilter} onValueChange={setTopicFilter}>
            <SelectTrigger className="w-48 bg-white border-slate-200">
              <SelectValue placeholder="All Topics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Topics</SelectItem>
              {topics.map((topic) => (
                <SelectItem key={topic.key} value={topic.key}>
                  {topic.display}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 bg-white border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sheet List */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="text-center py-12 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : sheets.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Reference Sheets</h3>
              <p className="text-slate-500 mb-4">
                {search || topicFilter !== 'all' || statusFilter !== 'all'
                  ? 'No sheets match your filters.'
                  : 'Create your first reference sheet to get started.'}
              </p>
              {!search && topicFilter === 'all' && statusFilter === 'all' && (
                <Button onClick={openCreateDialog} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Reference Sheet
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sheets.map((sheet) => (
              <Card
                key={sheet.sheet_id}
                className="bg-white border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`sheet-card-${sheet.sheet_id}`}
              >
                <CardContent className="p-4">
                  {/* Thumbnail */}
                  <div className="aspect-[4/3] bg-slate-100 rounded-lg mb-3 overflow-hidden">
                    {sheet.png_urls?.length > 0 ? (
                      <img
                        src={sheet.png_urls[0]}
                        alt={sheet.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{sheet.title}</h3>
                    <Badge 
                      variant={sheet.status === 'published' ? 'default' : 'secondary'}
                      className={sheet.status === 'published' 
                        ? 'bg-green-100 text-green-700 border-green-200' 
                        : 'bg-amber-100 text-amber-700 border-amber-200'}
                    >
                      {sheet.status}
                    </Badge>
                  </div>
                  
                  {sheet.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-2">{sheet.description}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                      {getTopicDisplay(sheet.topic_key)}
                    </Badge>
                    {sheet.subtopic_key && (
                      <Badge variant="outline" className="bg-teal-50 text-teal-600 border-teal-200 text-xs">
                        {sheet.subtopic_key}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-3 text-slate-400 text-sm">
                      <span className="flex items-center gap-1">
                        <Image className="w-4 h-4" />
                        {sheet.png_urls?.length || 0}
                      </span>
                      {sheet.pdf_url && (
                        <span className="flex items-center gap-1 text-green-600">
                          <Download className="w-4 h-4" />
                          PDF
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`/reference-sheets/${sheet.sheet_id}`, '_blank')}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid={`preview-sheet-${sheet.sheet_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(sheet)}
                        className="text-slate-500 hover:text-teal-600"
                        data-testid={`edit-sheet-${sheet.sheet_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedSheet(sheet);
                          setShowDeleteDialog(true);
                        }}
                        className="text-slate-500 hover:text-red-600"
                        data-testid={`delete-sheet-${sheet.sheet_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex justify-center gap-2 pt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="border-slate-200"
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-slate-500">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === pagination.total_pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="border-slate-200"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {selectedSheet ? 'Edit Reference Sheet' : 'Create Reference Sheet'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Upload images for viewing and optional PDF for download.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <Label className="text-slate-700">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., ACLS Algorithm Card"
                className="mt-1 bg-white border-slate-200"
                data-testid="sheet-title-input"
              />
            </div>
            
            {/* Description */}
            <div>
              <Label className="text-slate-700">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of this reference sheet..."
                className="mt-1 min-h-[60px] bg-white border-slate-200"
              />
            </div>

            {/* Topic & Subtopic */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Topic *</Label>
                <Select
                  value={formData.topic_key}
                  onValueChange={(v) => setFormData(p => ({ ...p, topic_key: v, subtopic_key: '' }))}
                >
                  <SelectTrigger className="mt-1 bg-white border-slate-200">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((topic) => (
                      <SelectItem key={topic.key} value={topic.key}>
                        {topic.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Subtopic (optional)</Label>
                <Select
                  value={formData.subtopic_key || "none"}
                  onValueChange={(v) => setFormData(p => ({ ...p, subtopic_key: v === "none" ? "" : v }))}
                  disabled={!formData.topic_key}
                >
                  <SelectTrigger className="mt-1 bg-white border-slate-200">
                    <SelectValue placeholder="Select subtopic" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {subtopics.map((st) => (
                      <SelectItem key={st.key} value={st.key}>
                        {st.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status */}
            <div>
              <Label className="text-slate-700">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}
              >
                <SelectTrigger className="mt-1 w-40 bg-white border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Image Upload */}
            <div>
              <Label className="text-slate-700">Images (PNG/JPG/WEBP) *</Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  data-testid="image-upload-input"
                />
                <label 
                  htmlFor="image-upload" 
                  className={`flex flex-col items-center cursor-pointer py-4 ${uploading ? 'pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-teal-500 animate-spin mb-2" />
                      <span className="text-sm text-slate-600">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="text-sm text-slate-600">Click to upload images</span>
                      <span className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP (max 10MB each)</span>
                    </>
                  )}
                </label>
                
                {formData.png_urls.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {formData.png_urls.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={url}
                          alt={`Upload ${idx + 1}`}
                          className="w-full aspect-square object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Upload images in order. First image will be the cover.
              </p>
            </div>

            {/* PDF Upload */}
            <div>
              <Label className="text-slate-700">PDF (Optional - for download)</Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4">
                {formData.pdf_url ? (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Download className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-slate-600">PDF attached</span>
                      <a 
                        href={formData.pdf_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-teal-600 hover:underline"
                      >
                        Preview
                      </a>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removePdf}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                      id="pdf-upload"
                      data-testid="pdf-upload-input"
                    />
                    <label 
                      htmlFor="pdf-upload" 
                      className={`flex flex-col items-center cursor-pointer py-2 ${uploading ? 'pointer-events-none' : ''}`}
                    >
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-sm text-slate-600">Click to upload PDF</span>
                          <span className="text-xs text-slate-400 mt-1">Max 20MB</span>
                        </>
                      )}
                    </label>
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || uploading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="save-sheet-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                selectedSheet ? 'Update Sheet' : 'Create Sheet'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Delete Reference Sheet?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will permanently delete "{selectedSheet?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
